import { Check } from 'lucide-react';
import { cn } from '@paulownia/shared/lib/utils';

interface WizardStep {
  id: string;
  label: string;
}

interface WizardProgressProps {
  steps: WizardStep[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
}

export function WizardProgress({ steps, currentStep, onStepClick }: WizardProgressProps) {
  return (
    <nav className="flex items-center justify-center w-full">
      <ol className="flex items-center justify-between w-full max-w-md">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isClickable = onStepClick && isCompleted;

          return (
            <li key={step.id} className="flex items-center flex-1 last:flex-none">
              {/* Step indicator */}
              <button
                type="button"
                onClick={() => isClickable && onStepClick(index)}
                disabled={!isClickable}
                className={cn(
                  'flex items-center gap-2 px-2 py-1 rounded-lg transition-colors',
                  isClickable && 'hover:bg-gray-100 cursor-pointer',
                  !isClickable && 'cursor-default'
                )}
              >
                <div
                  className={cn(
                    'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors flex-shrink-0',
                    isCompleted && 'bg-blue-500 text-white',
                    isCurrent && 'bg-blue-500 text-white ring-2 ring-blue-200',
                    !isCompleted && !isCurrent && 'bg-gray-200 text-gray-500'
                  )}
                >
                  {isCompleted ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={cn(
                    'text-sm font-medium whitespace-nowrap hidden sm:inline',
                    isCurrent && 'text-blue-600',
                    isCompleted && 'text-gray-600',
                    !isCompleted && !isCurrent && 'text-gray-400'
                  )}
                >
                  {step.label}
                </span>
              </button>

              {/* Connector line (not for last step) */}
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 min-w-4',
                    isCompleted ? 'bg-blue-500' : 'bg-gray-200'
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
